/**
 * Enhanced Dice Roller Store - Database-backed configuration and roll logging
 * Maintains full compatibility with GlobalDiceRoller plugin while adding database persistence
 */
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useDiceRollerStore as _useDiceRollerStore } from '../components/plugins/GlobalDiceRoller/store'
import { useUserStore } from './userStore'
import DiceConfigService from '@/services/dice/DiceConfigService'
import DiceAnalyticsService from '@/services/dice/DiceAnalyticsService'
import { initDatabase, saveToDb, getFromDb, getEntry } from '@/utils/indexedDBHelper'
import { auth } from '@/services/firebase/config'
import { getIdTokenResult, onAuthStateChanged } from 'firebase/auth'

export const useDiceRollerStore = defineStore('diceRollerEnhanced', () => {
  // Get the autonomous plugin store (don't interfere with it)
  const pluginStore = _useDiceRollerStore()
  const userStore = useUserStore()

  // Enhanced state for DB persistence and critical stats
  const criticalStats = ref(null)
  const isLoadingConfig = ref(false)
  const isSavingConfig = ref(false)
  const isLoadingStats = ref(false)

  // Store pending metadata for quickRoll
  const pendingRollMetadata = ref(null)

  // Auto-save configuration when core store settings change
  const saveCurrentConfigToDatabase = async () => {
    if (!userStore.user?.uid || isSavingConfig.value) return

    // If we're applying a remote configuration, do not auto-save back to DB
    if (applyingRemoteConfig.value) return

    try {
      isSavingConfig.value = true
      console.log('Auto-saving dice configuration to database')

      // Debug the plugin store values
      console.log('Plugin store values:', {
        activeDiceSkin: pluginStore.activeDiceSkin,
        diceColor: pluginStore.diceColor,
        letterColor: pluginStore.letterColor,
        playDiceSound: pluginStore.playDiceSound,
        use3DMode: pluginStore.use3DMode
      })

      const configToSave = {
        diceSkin: pluginStore.activeDiceSkin?.value || 'default',
        diceColor: pluginStore.diceColor || '#ffffff',
        letterColor: pluginStore.letterColor || '#000000',
        playSound: pluginStore.playDiceSound ?? true,
        use3DMode: pluginStore.use3DMode ?? true
      }

      await DiceConfigService.saveUserConfig(configToSave)

      // Optionally persist the new config locally as well to keep IndexedDB in sync
      if (persistLocalOnChange.value) {
        try {
          await saveLocalConfig(userStore.user?.uid, configToSave)
        } catch (e) {
          console.warn('Failed to persist local config after change:', e)
        }
      }
    } catch (error) {
      console.error('Auto-save failed:', error)
    } finally {
      isSavingConfig.value = false
    }
  }

  // Auto-save watcher management: register watchers only after initialization
  let autoSaveWatchStops = []
  let autoSaveWatchersSet = false

  const setupAutoSaveWatchers = () => {
    if (autoSaveWatchersSet) return
    try {
      autoSaveWatchStops = [
        watch(() => pluginStore.activeDiceSkin, saveCurrentConfigToDatabase, { deep: true }),
        watch(() => pluginStore.diceColor, saveCurrentConfigToDatabase),
        watch(() => pluginStore.letterColor, saveCurrentConfigToDatabase),
        watch(() => pluginStore.playDiceSound, saveCurrentConfigToDatabase),
        watch(() => pluginStore.use3DMode, saveCurrentConfigToDatabase)
      ]
      autoSaveWatchersSet = true
    } catch (e) {
      console.warn('[DiceRollerStore] Failed to setup auto-save watchers', e)
    }
  }

  const teardownAutoSaveWatchers = () => {
    try {
      autoSaveWatchStops.forEach((stop) => stop && stop())
    } catch (e) {
      /* noop */
    }
    autoSaveWatchStops = []
    autoSaveWatchersSet = false
  }

  // IndexedDB for local caching of user dice configuration
  const DB_NAME = 'dice_roller_db'
  const DB_VERSION = 1
  const STORE_NAME = 'configs'
  // Local cache TTL: avoid remote fetch when local cache is recent (default 24 hours)
  const LOCAL_CACHE_TTL_MS = 24 * 60 * 60 * 1000

  const applyingRemoteConfig = ref(false)
  // --- LocalStorage fast-path cache (in addition to IndexedDB) ---
  const getLocalStorageConfig = (uid) => {
    try {
      if (!uid || typeof window === 'undefined') return null
      const raw = window.localStorage.getItem(`dice_cfg_${uid}`)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object') return null
      return { data: parsed.data || null, timestamp: Number(parsed.ts) || 0 }
    } catch {
      return null
    }
  }

  const saveLocalStorageConfig = (uid, config) => {
    try {
      if (!uid || typeof window === 'undefined') return
      const payload = { ts: Date.now(), data: config }
      window.localStorage.setItem(`dice_cfg_${uid}`, JSON.stringify(payload))
    } catch {
      /* ignore */
    }
  }
  // Option: when true, immediately persist local IndexedDB copy when user changes settings
  // Default to true so changes persist locally by default (no UI toggle required)
  const persistLocalOnChange = ref(true)
  // Track IndexedDB initialization state similar to other versioned stores
  const dbInitialized = ref(false)
  // Create a stateRef proxy so the helper can update the reactive ref
  const dbStateRef = {}
  Object.defineProperty(dbStateRef, 'dbInitialized', {
    get: () => dbInitialized.value,
    set: (v) => {
      dbInitialized.value = v
    },
    configurable: true
  })

  const initLocalDatabase = async () => {
    try {
      await initDatabase(DB_NAME, DB_VERSION, STORE_NAME, dbStateRef)
    } catch (e) {
      console.warn('[DiceRollerStore] IndexedDB init failed', e)
    }
  }

  // Returns { data, timestamp } or null
  const getLocalConfig = async (uid) => {
    if (!uid) return null
    try {
      await initLocalDatabase()
      const cacheKey = `user_config_${uid}`
      const entry = await getEntry(DB_NAME, STORE_NAME, cacheKey)
      if (!entry) return null
      return { data: entry.data || null, timestamp: entry.timestamp || 0 }
    } catch (e) {
      console.warn('Failed to read local dice config from IndexedDB', e)
      return null
    }
  }

  const saveLocalConfig = async (uid, config) => {
    if (!uid || !config) return
    try {
      await initLocalDatabase()
      const cacheKey = `user_config_${uid}`
      await saveToDb(DB_NAME, STORE_NAME, cacheKey, 'user', uid, config)
      // Mirror to localStorage for fast-path next load
      try {
        saveLocalStorageConfig(uid, config)
      } catch {}
    } catch (e) {
      console.warn('Failed to save local dice config to IndexedDB', e)
    }
  }

  // Database-backed configuration methods with full API compatibility
  const setDiceSkin = async (skin) => {
    // Call plugin store method and let watcher handle database save
    return pluginStore.setDiceSkin(skin)
  }

  const setDiceColor = async (color) => {
    // Call plugin store method and let watcher handle database save
    pluginStore.setDiceColor(color)
  }

  const setLetterColor = async (color) => {
    // Call plugin store method and let watcher handle database save
    pluginStore.setLetterColor(color)
  }

  const setPlaySound = async (enabled) => {
    // Call plugin store method and let watcher handle database save
    pluginStore.setPlayDiceSound(enabled)
  }

  const setUse3DMode = async (enabled) => {
    // Call plugin store method and let watcher handle database save
    pluginStore.setUse3DMode(enabled)
  }

  // API compatibility is provided by the spread operator in return statement
  // Configuration loading - directly set core store state without localStorage saves
  const loadUserConfiguration = async (userId, attempt = 1) => {
    if (!userId || isLoadingConfig.value) return

    try {
      isLoadingConfig.value = true
      // First, try to apply a quick localStorage copy (fast startup). If fresh, skip remote.
      const lsEntry = getLocalStorageConfig(userId)
      if (lsEntry && lsEntry.data) {
        try {
          const now = Date.now()
          if (lsEntry.timestamp && now - lsEntry.timestamp <= LOCAL_CACHE_TTL_MS) {
            applyingRemoteConfig.value = true
            const cfg = lsEntry.data
            if (cfg.diceSkin) {
              const skinObj = pluginStore.getSkinByValue
                ? pluginStore.getSkinByValue(cfg.diceSkin)
                : null
              pluginStore.activeDiceSkin = skinObj || cfg.diceSkin
            }
            if (cfg.diceColor) pluginStore.diceColor = cfg.diceColor
            if (cfg.letterColor) pluginStore.letterColor = cfg.letterColor
            if (typeof cfg.playSound === 'boolean') pluginStore.playDiceSound = cfg.playSound
            if (typeof cfg.use3DMode === 'boolean') pluginStore.use3DMode = cfg.use3DMode
            return
          }
        } finally {
          applyingRemoteConfig.value = false
        }
      }

      // Fallback: try IndexedDB local cache (also fast and offline friendly)
      const localEntry = await getLocalConfig(userId)
      // Ensure localConfig is available in this function scope (used later)
      let localConfig = null
      if (localEntry && localEntry.data) {
        try {
          applyingRemoteConfig.value = true
          // Populate outer-scoped variable rather than declaring a new one
          localConfig = localEntry.data
          // Assign values directly to avoid invoking plugin setters
          // which write to localStorage during initialization
          if (localConfig.diceSkin) {
            const skinObj = pluginStore.getSkinByValue
              ? pluginStore.getSkinByValue(localConfig.diceSkin)
              : null
            pluginStore.activeDiceSkin = skinObj || localConfig.diceSkin
          }
          if (localConfig.diceColor) pluginStore.diceColor = localConfig.diceColor
          if (localConfig.letterColor) pluginStore.letterColor = localConfig.letterColor
          if (typeof localConfig.playSound === 'boolean')
            pluginStore.playDiceSound = localConfig.playSound
          if (typeof localConfig.use3DMode === 'boolean')
            pluginStore.use3DMode = localConfig.use3DMode
        } finally {
          // Allow future user changes to be saved
          applyingRemoteConfig.value = false
        }

        // If the IDB cache entry is recent, skip the remote fetch to avoid
        // unnecessary network reads on page load. This keeps the local-first
        // behavior while reducing remote traffic when a fresh cache exists.
        try {
          const now = Date.now()
          if (localEntry.timestamp && now - localEntry.timestamp <= LOCAL_CACHE_TTL_MS) {
            console.debug('[DiceRollerStore] Local config is fresh; skipping remote fetch')
            // Also ensure localStorage mirror is up to date for the next boot
            try {
              saveLocalStorageConfig(userId, localConfig)
            } catch {}
            return
          }
        } catch (e) {
          // If any error occurs checking the timestamp, fall through and fetch remote
        }
      }

      // Now fetch authoritative user config from rules DB (Firestore)
      const config = await DiceConfigService.getUserConfig(userId)

      if (config) {
        try {
          console.log('Loaded dice configuration from database (remote):', config)

          // Use updatedAt timestamps when available to decide whether to apply remote
          const remoteUpdatedAtMs = config.updatedAt ? Date.parse(config.updatedAt) : 0
          const localUpdatedAtMs =
            localConfig && localConfig.updatedAt
              ? Date.parse(localConfig.updatedAt)
              : localEntry?.timestamp || 0

          const remoteIsNewer = remoteUpdatedAtMs > localUpdatedAtMs

          if (remoteIsNewer) {
            try {
              applyingRemoteConfig.value = true
              // Assign directly to avoid localStorage writes during init
              if (config.diceSkin) {
                const skinObj = pluginStore.getSkinByValue
                  ? pluginStore.getSkinByValue(config.diceSkin)
                  : null
                pluginStore.activeDiceSkin = skinObj || config.diceSkin
              }
              if (config.diceColor) pluginStore.diceColor = config.diceColor
              if (config.letterColor) pluginStore.letterColor = config.letterColor
              if (typeof config.playSound === 'boolean')
                pluginStore.playDiceSound = config.playSound
              if (typeof config.use3DMode === 'boolean') pluginStore.use3DMode = config.use3DMode
              // Persist authoritative remote config into local IndexedDB (only when remote changed)
              await saveLocalConfig(userId, config)
              console.log('[DiceRollerStore] Applied newer remote dice configuration')
            } finally {
              applyingRemoteConfig.value = false
            }
          } else {
            // Remote not newer. Ensure local copy exists in IndexedDB if missing.
            if (!localConfig) {
              await saveLocalConfig(userId, config)
            } else {
              console.debug('[DiceRollerStore] Local config is up-to-date; skipping remote apply')
              // Refresh local fast-path timestamp so future reloads skip remote entirely
              try {
                saveLocalStorageConfig(userId, localConfig)
              } catch {}
            }
          }
        } catch (e) {
          console.warn('[DiceRollerStore] Error while processing remote config', e)
        }
      }
    } catch (error) {
      const code = String(error?.code || error?.name || '').toLowerCase()
      const msg = String(error?.message || '')
      if (code.includes('permission') || msg.toLowerCase().includes('permission')) {
        // Likely auth/rules race; retry with small backoff after refreshing auth token
        try {
          await ensureAuthReady(userId)
        } catch {}
        const delayMs = Math.min(800, 150 * attempt * attempt)
        console.debug(
          `[DiceRollerStore] Permissions not ready (attempt ${attempt}). Retrying in ${delayMs}ms...`
        )
        await new Promise((r) => setTimeout(r, delayMs))
        if (attempt < 4) {
          return await loadUserConfiguration(userId, attempt + 1)
        }
        console.warn('[DiceRollerStore] Gave up loading dice config after retries')
      } else {
        console.error('Failed to load dice configuration:', error)
      }
    } finally {
      isLoadingConfig.value = false
    }
  }

  // Initialize configuration when user logs in or store initializes
  const ensureAuthReady = async (expectedUid) => {
    try {
      // Wait for a currentUser with a matching uid
      if (!auth.currentUser || (expectedUid && auth.currentUser.uid !== expectedUid)) {
        await new Promise((resolve) => {
          const off = onAuthStateChanged(auth, () => {
            try {
              off && off()
            } catch {}
            resolve()
          })
        })
      }
      // Force-refresh token/claims once to avoid rules races
      if (auth.currentUser) {
        await getIdTokenResult(auth.currentUser, true).catch(() => {})
      }
    } catch {
      /* non-fatal */
    }
  }

  const initializeUserConfiguration = async (userId) => {
    if (!userId) return

    // Small delay to allow auth claims/rules to be ready and reduce race conditions
    try {
      await ensureAuthReady(userId)
      await new Promise((r) => setTimeout(r, 50))
    } catch {}
    // Load user configuration (local fast load + remote authoritative)
    await loadUserConfiguration(userId)

    // If no remote configuration exists, do not force-write a local cache here.
    // Keep defaults in-memory and let the user save (which will write remote DB).
    // Previously this function would save defaults to the remote DB when no
    // config existed. That caused an automatic write on page load. Do not
    // auto-write defaults — keep defaults in-memory and only persist when the
    // user explicitly changes settings.
  }

  // Initialize configuration when auth becomes ready; clear on logout/not-ready
  watch(
    () => userStore.authReady,
    async (ready) => {
      if (ready && userStore.user?.uid) {
        console.log('Auth ready, initializing dice configuration:', userStore.user.uid)
        await initializeUserConfiguration(userStore.user.uid)
        try {
          setupAutoSaveWatchers()
        } catch (e) {
          console.warn('[DiceRollerStore] setupAutoSaveWatchers failed after authReady', e)
        }
      } else if (!ready) {
        console.debug('Auth not ready; dice config initialization deferred')
        criticalStats.value = null
        try {
          teardownAutoSaveWatchers()
        } catch (e) {
          /* noop */
        }
      }
    },
    { immediate: true }
  )

  // Current context tracking (for roll history filtering)
  const currentContext = ref({
    characterId: null,
    campaignId: null,
    sessionId: null
  })

  // Function to set the current context (called when dice roller opens)
  const setCurrentContext = (context = {}) => {
    currentContext.value = {
      characterId: context.characterId || null,
      campaignId: context.campaignId || null,
      sessionId: context.sessionId || null
    }
    console.log('Updated current context:', currentContext.value)
  }

  // Function to get the current context for filtering
  const getCurrentContext = () => {
    return { ...currentContext.value }
  }

  // Legacy functions for backward compatibility
  const getCurrentCharacterId = () => currentContext.value.characterId
  const getCurrentCampaignId = () => currentContext.value.campaignId
  const getActiveSessionId = () => currentContext.value.sessionId

  // Enhanced roll history with database logging and context detection and context detection
  const addRollToHistoryWithLogging = async (roll) => {
    // Merge pending metadata into the roll
    const enhancedRoll = {
      ...roll,
      // Use pending metadata if available, otherwise fall back to roll properties
      notation: pendingRollMetadata.value?.notation || roll.notation || 'Manual Roll',
      note: pendingRollMetadata.value?.note || roll.note || '',
      rollType: pendingRollMetadata.value?.rollType || roll.rollType || 'random',
      rollContext: pendingRollMetadata.value?.rollContext || roll.rollContext || 'general',
      characterId: pendingRollMetadata.value?.characterId || roll.characterId,
      // Auto-detect campaign and session context
      campaignId: getCurrentCampaignId(),
      sessionId: getActiveSessionId(),
      rollSource: 'dice_roller'
    }

    // Clear pending metadata after use
    pendingRollMetadata.value = null

    pluginStore.addRollToHistory(enhancedRoll)

    try {
      // Only save to database if we have valid data (avoid Firestore undefined values)
      if (enhancedRoll.total !== undefined && enhancedRoll.notation) {
        await DiceAnalyticsService.logRoll({
          expression: enhancedRoll.notation,
          result: enhancedRoll.total,
          individualRolls: enhancedRoll.rawResults || [],
          modifier: enhancedRoll.modifier || 0,
          note: enhancedRoll.note,
          rollType: enhancedRoll.rollType,
          rollContext: enhancedRoll.rollContext,
          characterId: enhancedRoll.characterId,
          campaignId: enhancedRoll.campaignId,
          sessionId: enhancedRoll.sessionId,
          rollSource: enhancedRoll.rollSource
        })
      }
    } catch (error) {
      console.error('Failed to log roll to database:', error)
    }
  } // Override the reportDieSettled method to include database logging
  const reportDieSettled = (rawValue, dieId = null) => {
    pluginStore.reportDieSettled(rawValue, dieId)

    if (
      pluginStore.activeRoll &&
      pluginStore.activeRoll.collectedResults.length >= pluginStore.activeRoll.expectedDiceCount
    ) {
      const completedRollData = pluginStore.activeRoll
      const sumOfRawResults = completedRollData.collectedResults.reduce((sum, val) => sum + val, 0)
      const finalTotal = sumOfRawResults + completedRollData.totalBaseModifier

      setTimeout(async () => {
        try {
          await DiceAnalyticsService.logRoll({
            expression: completedRollData.metadata.notation,
            result: finalTotal,
            individualRolls: completedRollData.collectedResults.map((value) => ({
              value,
              sides: 20,
              roll: value
            })),
            modifier: completedRollData.totalBaseModifier,
            note: completedRollData.metadata.note || '',
            rollType: completedRollData.metadata.rollType || 'standard',
            rollContext: completedRollData.metadata.rollContext || 'general',
            characterId: completedRollData.metadata.characterId,
            campaignId: getCurrentCampaignId(),
            sessionId: getActiveSessionId(),
            rollSource: 'dice_roller'
          })
        } catch (error) {
          console.error('Failed to log completed roll to database:', error)
        }
      }, 100)
    }
  }

  // Critical stats
  const loadCriticalStats = async (characterId = null, timeRange = '30d') => {
    try {
      isLoadingStats.value = true
      let stats

      if (characterId) {
        stats = await DiceAnalyticsService.getCharacterStats(characterId)
      } else {
        const userStore = useUserStore()
        const uid = userStore.user?.uid
        if (uid) {
          stats = await DiceAnalyticsService.getUserStats(uid, timeRange)
        }
      }

      criticalStats.value = stats || null
      return stats
    } catch (error) {
      console.error('Failed to load critical stats:', error)
      return null
    } finally {
      isLoadingStats.value = false
    }
  }

  const criticalSuccessRate = computed(() => criticalStats.value?.d20CriticalSuccessRate || 0)
  const criticalFailureRate = computed(() => criticalStats.value?.d20CriticalFailureRate || 0)
  const totalCriticals = computed(() => {
    if (!criticalStats.value) return 0
    return criticalStats.value.d20CriticalSuccesses + criticalStats.value.d20CriticalFailures
  })
  const isLucky = computed(() => {
    const stats = criticalStats.value
    if (!stats || stats.d20Rolls < 20) return null
    return stats.d20CriticalSuccessRate > 5.0 || stats.d20CriticalFailureRate < 5.0
  })

  // --- Inline/silent evaluator (no overlay) ---
  // Parses dice notation, rolls immediately in JS, and returns a compact result object.
  // Supports a limited advantage/disadvantage mode for single 1d20 expressions.
  function rollSilent(notation, options = {}) {
    try {
      const expr = String(notation || '')
        .replace(/\s+/g, '')
        .toLowerCase()
      if (!expr) return null

      const baseMod = Number.isFinite(options.modifier) ? Number(options.modifier) : 0
      const diceTerms = Array.from(expr.matchAll(/([+-])?(\d*)d(\d+)/g))
      const flatTerms = Array.from(expr.matchAll(/([+-])\s*(\d+)(?!d)/g))

      // Build groups without dedup so we preserve counts for rolling
      const groups = diceTerms
        .map((t) => {
          const sign = t[1] === '-' ? -1 : 1 // sign ignored for dice groups; treat as additive
          const cnt = t[2] ? parseInt(t[2], 10) || 1 : 1
          const sides = parseInt(t[3], 10) || 0
          return { type: sides > 0 ? `d${sides}` : null, count: Math.max(1, Math.abs(cnt)), sign }
        })
        .filter((g) => !!g.type)

      // Flat modifier from trailing +N/-N segments
      const flat = flatTerms.reduce(
        (acc, m) => acc + (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) || 0),
        0
      )
      const finalMod = baseMod + flat

      // Advantage/disadvantage: only when expression is exactly 1d20 (+/-mod) i.e., one group of 1 x d20
      const adv = !!options.advantage
      const dis = !!options.disadvantage
      const isSingleD20 = groups.length === 1 && groups[0].type === 'd20' && groups[0].count === 1
      const useAdvMode = isSingleD20 && adv ^ dis // XOR

      const rawResults = []
      const details = {}

      // Roll function
      const rollDie = (sides) => 1 + Math.floor(Math.random() * Math.max(1, sides))

      for (const g of groups) {
        const sides = parseInt(g.type.substring(1), 10) || 0
        for (let i = 0; i < g.count; i++) {
          if (useAdvMode && g.type === 'd20') {
            const r1 = rollDie(20)
            const r2 = rollDie(20)
            const used = adv ? Math.max(r1, r2) : Math.min(r1, r2)
            rawResults.push(used)
            details.advantage = adv
            details.disadvantage = dis
            details.advRolls = [r1, r2]
          } else {
            rawResults.push(rollDie(sides))
          }
        }
      }

      const total = rawResults.reduce((a, b) => a + b, 0) + finalMod

      const result = {
        notation,
        rawResults,
        modifier: finalMod,
        total,
        ...(Object.keys(details).length ? { details } : {}),
        // Surface metadata if provided
        note: options.note,
        rollType: options.rollType,
        characterId: options.characterId
      }

      // Optionally add to in-memory history (no overlay, no notifier)
      if (options?.log !== false) {
        try {
          pluginStore.addRollToHistory({
            notation: result.notation,
            note: result.note || '',
            rawResults: result.rawResults,
            modifier: result.modifier,
            total: result.total,
            rollType: result.rollType || 'inline',
            rollSource: 'kb_inline',
            characterId: result.characterId
          })
        } catch (e) {
          /* non-fatal */
        }
      }

      return result
    } catch (e) {
      console.warn('[DiceRollerStore] rollSilent failed', e)
      return null
    }
  }

  return {
    // Re-export ALL core functionality with explicit overrides
    ...pluginStore,

    // Explicitly expose critical reactive properties (refs need explicit exposure)
    isVisible: computed(() => pluginStore.isVisible),
    diceToRoll: computed(() => pluginStore.diceToRoll),
    rollHistory: computed(() => pluginStore.rollHistory),
    activeDiceSkin: computed(() => pluginStore.activeDiceSkin),
    diceColor: computed(() => pluginStore.diceColor),
    letterColor: computed(() => pluginStore.letterColor),
    playDiceSound: computed(() => pluginStore.playDiceSound),
    use3DMode: computed(() => pluginStore.use3DMode),
    availableSkins: computed(() => pluginStore.availableSkins),
    allDiceTypes: computed(() => pluginStore.allDiceTypes),

    // Pending configuration properties
    pendingConfiguration: computed(() => pluginStore.pendingConfiguration),
    pendingDiceGroups: computed(() => pluginStore.pendingDiceGroups),
    pendingModifier: computed(() => pluginStore.pendingModifier),
    pendingDiceType: computed(() => pluginStore.pendingDiceType),
    pendingDiceCount: computed(() => pluginStore.pendingDiceCount),

    // Explicitly expose control methods
    // Wrapper for showDiceRoller that handles context clearing
    showDiceRoller: (options = {}) => {
      // If no character/campaign/session context provided, clear context for rogue rolls
      if (!options.characterId && !options.campaignId && !options.sessionId) {
        console.log('Opening dice roller without context - clearing context for rogue rolls')
        setCurrentContext({})
      }
      return pluginStore.showDiceRoller(options)
    },

    hideDiceRoller: pluginStore.hideDiceRoller,
    toggleDiceRoller: pluginStore.toggleDiceRoller,
    clearDice: pluginStore.clearDice,
    addDice: pluginStore.addDice,
    removeDieByIndex: pluginStore.removeDieByIndex,
    setRolling: pluginStore.setRolling,
    clearRollHistory: pluginStore.clearRollHistory,

    // Override methods with database versions
    setDiceSkin,
    setDiceColor,
    setLetterColor,
    setPlaySound,
    setUse3DMode,
    addRollToHistory: addRollToHistoryWithLogging,
    reportDieSettled,

    // Enhanced quickRoll with metadata support
    quickRoll: (input, options = {}) => {
      // Store metadata for later injection when roll completes
      try {
        const metadata = {
          notation: typeof input === 'string' ? input : (input && input.notation) || '',
          note: options.note || '',
          rollType: options.rollType || 'random',
          rollContext: options.rollContext || 'general',
          characterId: options.characterId,
          campaignId: options.campaignId || getCurrentCampaignId(),
          sessionId: options.sessionId || getActiveSessionId(),
          rollSource: options.rollSource || 'dice_roller'
        }

        pendingRollMetadata.value = metadata

        // Update current context with the character/campaign/session from this roll
        setCurrentContext({
          characterId: metadata.characterId,
          campaignId: metadata.campaignId,
          sessionId: metadata.sessionId
        })

        console.log('Stored pending metadata:', pendingRollMetadata.value)
        console.log('Updated context from quickRoll:', getCurrentContext())
      } catch (error) {
        console.error(
          'Error storing pending metadata:',
          error,
          'pendingRollMetadata:',
          pendingRollMetadata
        )
      }

      // Call the original quickRoll method
      return pluginStore.quickRoll(input, options)
    },

    // Enhanced features
    loadUserConfiguration,
    initializeUserConfiguration,
    loadCriticalStats,
    // Local persistence option (enabled by default)
    // Note: no UI toggle exposed; persistence occurs automatically
    // IndexedDB state
    dbInitialized: computed(() => dbInitialized.value),

    // Enhanced analytics methods
    getUserStats: (timeRange = 'lifetime') => {
      const userStore = useUserStore()
      const uid = userStore.user?.uid
      return uid ? DiceAnalyticsService.getUserStats(uid, timeRange) : null
    },

    getCharacterStats: (characterId) => {
      return DiceAnalyticsService.getCharacterStats(characterId)
    },

    getCampaignStats: (campaignId) => {
      return DiceAnalyticsService.getCampaignStats(campaignId)
    },

    getSessionStats: (sessionId) => {
      return DiceAnalyticsService.getSessionStats(sessionId)
    },

    getRollHistory: (filters = {}, limit = 50) => {
      return DiceAnalyticsService.getRollHistory(filters, limit)
    },

    clearAnalyticsHistory: (filters = {}) => {
      return DiceAnalyticsService.clearRollHistory(filters)
    },

    // Context detection methods
    getCurrentCharacterId,
    getCurrentCampaignId,
    getActiveSessionId,
    getCurrentContext,
    setCurrentContext,

    // State
    isLoadingConfig: computed(() => isLoadingConfig.value),
    isSavingConfig: computed(() => isSavingConfig.value),
    isLoadingStats: computed(() => isLoadingStats.value),

    // Critical stats
    criticalStats: computed(() => criticalStats.value),
    criticalSuccessRate,
    criticalFailureRate,
    totalCriticals,
    isLucky,
    // Inline evaluator
    rollSilent
  }
})
